const VIETNAM_TIMEZONE = 'Asia/Ho_Chi_Minh';

function formatVietnamParts(date: Date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: VIETNAM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
}

export function formatVietnamDateTime(value?: string | Date | null): string {
  if (!value) return '-';

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  const { year, month, day, hour, minute } = formatVietnamParts(parsed);
  return `${day}/${month}/${year} ${hour}:${minute}`;
}

export function formatVietnamDate(value?: string | Date | null): string {
  if (!value) return '-';

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: VIETNAM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(parsed);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return `${get('day')}/${get('month')}/${get('year')}`;
}

export function formatVietnamTime(value?: string | Date | null): string {
  if (!value) return '';

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: VIETNAM_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(parsed);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return `${get('hour')}:${get('minute')}`;
}

/** `datetime-local` value (yyyy-mm-ddThh:mm) from API date + optional HH:mm in Vietnam time. */
export function toVietnamDatetimeLocalValue(
  dateIso: string,
  time?: string | null,
): string {
  const parsed = new Date(dateIso);
  if (Number.isNaN(parsed.getTime())) {
    const datePart = dateIso.split('T')[0];
    if (!time?.trim()) return datePart;
    const hm = time.trim().slice(0, 5);
    return hm ? `${datePart}T${hm}` : datePart;
  }

  const dateParts = new Intl.DateTimeFormat('en-US', {
    timeZone: VIETNAM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(parsed);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    dateParts.find((part) => part.type === type)?.value ?? '';

  const datePart = `${get('year')}-${get('month')}-${get('day')}`;

  if (!time?.trim()) return datePart;

  const trimmed = time.trim();
  const hm =
    trimmed.includes('T')
      ? trimmed.split('T')[1]?.slice(0, 5)
      : trimmed.slice(0, 5);
  return hm ? `${datePart}T${hm}` : datePart;
}
